import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Check, X, RefreshCw, Trash2, Ban, Loader2, AlertTriangle, Eye, Pencil, Search, Building2, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { api, errText, STATUS_META, chipCls, btnDark, btnLight, inputCls, labelCls, fmtDateTime } from "./api";

function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={`${chipCls} ${m.cls}`} data-testid={`status-${status}`}>{m.label}</span>;
}

function DraftEditor({ contact, onChanged, readOnly }) {
  const d = contact.draft || {};
  const [subject, setSubject] = useState(d.subject || "");
  const [body, setBody] = useState(d.body_text || "");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const dirty = subject !== (d.subject || "") || body !== (d.body_text || "");
  const sent = !!contact.sent;
  const locked = readOnly || sent;

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok); onChanged(r); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const save = () => act("save", () => api.patchContact(contact.id, { subject, body_text: body }), "Draft saved");
  const approve = async () => {
    const body_ = dirty ? { subject, body_text: body, approved: true } : { approved: true };
    await act("approve", () => api.patchContact(contact.id, body_), "Approved");
  };
  const unapprove = () => act("unapprove", () => api.patchContact(contact.id, { approved: false }), "Approval removed");
  const redraft = async () => {
    await act("redraft", async () => { const r = await api.redraft(contact.id); setSubject(r.draft.subject); setBody(r.draft.body_text); return r; }, "Redrafted");
  };
  const showPreview = async () => {
    try { setPreview(await api.preview(contact.id)); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="mt-3 border-t border-[#F0EBDC] pt-3 space-y-3" data-testid={`draft-editor-${contact.id}`}>
      {contact.draft ? (
        <>
          <div>
            <label className={labelCls}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={locked} className={`${inputCls} disabled:bg-[#FAF7F1]`} data-testid={`draft-subject-${contact.id}`} />
          </div>
          <div>
            <label className={labelCls}>Body · signature and opt-out footer are added automatically on send</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} disabled={locked} rows={Math.min(18, Math.max(8, body.split("\n").length + 1))} className={`${inputCls} font-sans leading-relaxed resize-y disabled:bg-[#FAF7F1]`} data-testid={`draft-body-${contact.id}`} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!locked && (
              <>
                {contact.status === "approved" ? (
                  <button type="button" disabled={!!busy} onClick={unapprove} className={btnLight} data-testid={`unapprove-${contact.id}`}>
                    {busy === "unapprove" ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Remove approval
                  </button>
                ) : (
                  <button type="button" disabled={!!busy || !!contact.already_emailed_in} onClick={approve} className={btnDark} data-testid={`approve-${contact.id}`} title={contact.already_emailed_in ? `Already emailed in ${contact.already_emailed_in} — the send gate skips this address` : ""}>
                    {busy === "approve" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {contact.already_emailed_in ? "Auto-skipped" : dirty ? "Save & approve" : "Approve"}
                  </button>
                )}
                <button type="button" disabled={!!busy || !dirty} onClick={save} className={btnLight} data-testid={`save-draft-${contact.id}`}>
                  {busy === "save" ? <Loader2 size={12} className="animate-spin" /> : <Pencil size={12} />} Save edits
                </button>
                <button type="button" disabled={!!busy} onClick={redraft} className={btnLight} data-testid={`redraft-${contact.id}`}>
                  {busy === "redraft" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Redraft with AI
                </button>
              </>
            )}
            <button type="button" onClick={showPreview} className={btnLight} data-testid={`preview-${contact.id}`}>
              <Eye size={12} /> Preview as sent
            </button>
            <span className="ml-auto font-mono text-[10px] text-[#A09680]">{d.edited ? "edited · " : ""}drafted {fmtDateTime(d.drafted_at)}{d.model ? ` · ${d.model}` : ""}</span>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-[#5C6470]">
          No draft yet.{contact.draft_error && <span className="text-[#C84C21]">Last attempt failed: {contact.draft_error}</span>}
          {!readOnly && (
            <button type="button" disabled={!!busy} onClick={redraft} className={btnDark} data-testid={`draft-now-${contact.id}`}>
              {busy === "redraft" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Draft with AI
            </button>
          )}
        </div>
      )}
      {preview && (
        <div className="bg-white border border-[#E8E1D2]" data-testid={`preview-pane-${contact.id}`}>
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#F0EBDC]">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680]">Exactly as it will be sent · {preview.subject}</span>
            <button type="button" onClick={() => setPreview(null)} className="text-[#A09680] hover:text-[#0F1825]"><X size={14} /></button>
          </div>
          <div className="p-4" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </div>
      )}
    </div>
  );
}

function ContactRow({ contact, onChanged, readOnly }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const act = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok); onChanged(r); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const short = contact.email;
  return (
    <li className={`px-4 py-3 ${open ? "bg-[#FAF7F1]" : "bg-white"} border-t border-[#F0EBDC]`} data-testid={`contact-row-${contact.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex-1 min-w-[240px] text-left flex items-start gap-3" data-testid={`contact-toggle-${contact.id}`} aria-expanded={open}>
          <ChevronDown size={14} className={`mt-1 shrink-0 text-[#A09680] transition-transform ${open ? "rotate-180" : ""}`} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[13.5px] text-[#0F1825] font-medium">{contact.full_name || contact.email}</span>
              {contact.designation && <span className="text-[12px] text-[#5C6470]">{contact.designation}</span>}
              {contact.role_label && contact.role !== "other" && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[#4E378A] border border-[#E4DDF5] bg-[#F6F3FC] px-1.5 py-0.5" data-testid={`role-${contact.id}`}>{contact.role_label}</span>}
            </div>
            <div className="font-mono text-[10.5px] text-[#A09680] break-all">{short}{contact.draft?.subject && !open ? <span className="text-[#5C6470]"> · {contact.draft.subject}</span> : null}</div>
          </div>
        </button>
        <div className="flex flex-wrap items-center gap-1.5">
          {contact.already_emailed_in ? (
            <span className={`${chipCls} border-[#B7791F] bg-[#FFF6E5] text-[#B7791F]`} title="This address has a sent record in a past campaign — the send gate skips it automatically" data-testid={`already-emailed-${contact.id}`}>
              <AlertTriangle size={10} /> already emailed · {contact.already_emailed_in} · auto-skipped
            </span>
          ) : contact.previously_contacted && (
            <span className={`${chipCls} border-[#B7791F] bg-[#FFF6E5] text-[#B7791F]`} title={`Emailed before in ${contact.previously_contacted_in || "a previous campaign"}`} data-testid={`previously-contacted-${contact.id}`}>
              <AlertTriangle size={10} /> Previously contacted
            </span>
          )}
          {contact.suggested_dnc && <span className={`${chipCls} border-[#C84C21] bg-white text-[#C84C21]`} data-testid={`suggested-dnc-${contact.id}`}>Opt-out language · suggest DNC</span>}
          {contact.requeued && !contact.sent && <span className={`${chipCls} border-[#B7791F] bg-[#FFF6E5] text-[#B7791F]`} title={`${contact.requeued.reason} · ${fmtDateTime(contact.requeued.at)}`} data-testid={`requeued-${contact.id}`}><RotateCcw size={10} /> Re-queued · {contact.requeued.reason}</span>}
          {contact.auto_suppressed_at && <span className={`${chipCls} border-[#C84C21] bg-[#FDECE6] text-[#C84C21]`} title={`Reply asked to be removed · ${fmtDateTime(contact.auto_suppressed_at)}`} data-testid={`auto-dnc-${contact.id}`}><Ban size={10} /> Opted out by reply · auto-DNC</span>}
          <StatusChip status={contact.status} />
          {contact.sent?.at && <span className="font-mono text-[10px] text-[#A09680]">{fmtDateTime(contact.sent.at)}</span>}
        </div>
        {!readOnly && (
          <span className="inline-flex items-center gap-3 ml-1">
            {contact.status !== "suppressed" ? (
              <button type="button" disabled={!!busy} onClick={() => act("dnc", () => api.patchContact(contact.id, { dnc: true }), "Added to do-not-contact list — never emailed again in any campaign")} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] hover:text-[#C84C21] inline-flex items-center gap-1" title="Do not contact — suppress this address for every current and future campaign" data-testid={`dnc-${contact.id}`}>
                <Ban size={11} /> DNC
              </button>
            ) : (
              <button type="button" disabled={!!busy} onClick={() => act("undnc", () => api.patchContact(contact.id, { dnc: false }), "Removed from do-not-contact list")} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] hover:text-[#0F1825]" data-testid={`undnc-${contact.id}`}>Allow</button>
            )}
            {!contact.sent && (
              <button type="button" disabled={!!busy} onClick={() => { if (window.confirm(`Remove ${contact.email}?`)) act("del", () => api.deleteContact(contact.id), "Contact removed"); }} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] hover:text-[#C84C21] inline-flex items-center gap-1" data-testid={`delete-contact-${contact.id}`}>
                <Trash2 size={11} />
              </button>
            )}
          </span>
        )}
      </div>
      {contact.reply_snippet && (
        <div className="mt-2 ml-7 text-[12px] text-[#4E378A] bg-[#EDE9F4] border-l-2 border-[#4E378A] px-3 py-1.5" data-testid={`reply-${contact.id}`}>
          <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] mr-2">Reply {fmtDateTime(contact.replied_at)}</span>{contact.reply_snippet}
        </div>
      )}
      {contact.bounce?.reason && contact.status === "bounced" && (
        <div className="mt-2 ml-7 text-[12px] text-[#C84C21]" data-testid={`bounce-${contact.id}`}>
          <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] mr-2">Bounced {fmtDateTime(contact.bounce.at)}</span>{contact.bounce.reason}
          {contact.bounce.excerpt && <span className="block text-[11px] text-[#A09680] mt-0.5 break-words">“{contact.bounce.excerpt}”</span>}
        </div>
      )}
      {contact.send_error && <div className="mt-2 ml-7 text-[12px] text-[#B7791F]">{contact.send_error}</div>}
      {open && <div className="ml-7"><DraftEditor contact={contact} onChanged={onChanged} readOnly={readOnly} /></div>}
    </li>
  );
}

function AssetPanel({ group, onChanged, readOnly }) {
  const [hook, setHook] = useState(group.asset_hook || "");
  const [busy, setBusy] = useState(false);
  const dirty = hook !== (group.asset_hook || "");
  const assets = group.assets || [];
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.setHook(group.id, hook);
      toast.success(r.unsent_drafts ? `Asset hook saved — redraft ${r.unsent_drafts} unsent draft${r.unsent_drafts === 1 ? "" : "s"} to apply it` : "Asset hook saved");
      onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="px-4 pb-3 border-t border-[#F0EBDC] pt-3 space-y-2" data-testid={`asset-panel-${group.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]">Asset hook · injected into every draft</span>
        {group.asset_researched_at && <span className="font-mono text-[9.5px] text-[#A09680]">Sonar · {fmtDateTime(group.asset_researched_at)}</span>}
        {group.asset_error && <span className="font-mono text-[9.5px] text-[#C84C21]" data-testid={`asset-error-${group.id}`}>live research failed — {group.asset_error}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <textarea value={hook} onChange={(e) => setHook(e.target.value)} disabled={readOnly} rows={2} placeholder="e.g. Molecule in indication (Phase 3; filing 2026)" className={`${inputCls} flex-1 min-w-[280px] resize-y`} data-testid={`asset-hook-${group.id}`} />
        {!readOnly && (
          <button type="button" disabled={busy || !dirty} onClick={save} className={btnLight} data-testid={`save-hook-${group.id}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save hook
          </button>
        )}
      </div>
      {assets.length > 0 && (
        <ul className="space-y-1" data-testid={`assets-${group.id}`}>
          {assets.map((a, i) => (
            <li key={`${a.molecule}-${i}`} className="text-[12px] text-[#5C6470] flex flex-wrap items-baseline gap-x-2">
              <span className="text-[#0F1825] font-medium">{a.molecule}</span>
              {a.indication && <span>· {a.indication}</span>}
              {a.stage && <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#A09680]">{a.stage}</span>}
              {a.milestone && <span>· {a.milestone}</span>}
              {(a.source_urls || []).slice(0, 3).map((u, j) => (
                <a key={u} href={u} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-[#C84C21] hover:text-[#0F1825]" data-testid={`asset-source-${group.id}-${i}-${j}`}>[{j + 1}]</a>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompanyCard({ group, onChanged, readOnly }) {
  const [showBrief, setShowBrief] = useState(false);
  const [busy, setBusy] = useState(false);
  const removeCompany = async () => {
    const unsentN = group.contacts.filter((c) => !c.sent).length;
    if (!window.confirm(`Delete ${group.name} entirely?\n\n• ${group.contacts.length} address${group.contacts.length === 1 ? "" : "es"} go on the blocklist (never emailed again)\n• the company's email domain is blocked for every future upload\n• ${unsentN} unsent contact${unsentN === 1 ? "" : "s"} removed; sent records are kept for history\n\nYou can unblock the domain later from the Blocklist panel.`)) return;
    setBusy(true);
    try { const r = await api.deleteCompany(group.company_key); toast.success(`${group.name} deleted · ${r.suppressed} addresses blocked${r.domains_blocked?.length ? ` · domain @${r.domains_blocked.join(", @")} blocked` : ""}`); onChanged(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const b = group.brief;
  const n = group.contacts.length;
  const approved = group.contacts.filter((c) => c.status === "approved").length;
  const sent = group.contacts.filter((c) => c.sent).length;
  const research = async () => {
    setBusy(true);
    try { await api.researchCompany(group.id); toast.success("Live asset research + brief refreshed"); onChanged(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white border border-[#E8E1D2]" data-testid={`company-card-${group.company_key.replace(/\s+/g, "-")}`}>
      <header className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Building2 size={15} className="text-[#C84C21] shrink-0" />
        <h3 className="font-serif text-[1.1rem] text-[#0F1825]">{group.name}</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]">{n} contact{n === 1 ? "" : "s"} · {approved} approved · {sent} sent</span>
        <span className="ml-auto inline-flex items-center gap-3">
          {b ? (
            <button type="button" onClick={() => setShowBrief((v) => !v)} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21] hover:text-[#0F1825]" data-testid={`toggle-brief-${group.id}`}>
              {showBrief ? "Hide research brief" : `Research brief · ${b.confidence || "medium"} confidence`}
            </button>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]">No research brief yet</span>
          )}
          {!readOnly && (
            <button type="button" disabled={busy} onClick={research} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] hover:text-[#0F1825] inline-flex items-center gap-1" data-testid={`research-company-${group.id}`} title="Live asset research (Sonar) + Claude brief">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} {b ? "Refresh with Sonar" : "Research with Sonar"}
            </button>
          )}
          {!readOnly && (
            <button type="button" disabled={busy} onClick={removeCompany} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] hover:text-[#C84C21] inline-flex items-center gap-1" data-testid={`delete-company-${group.id}`} title="Delete the whole company: block every address and its email domain">
              <Trash2 size={11} /> Delete company
            </button>
          )}
        </span>
      </header>
      {(b || group.asset_researched_at || group.asset_hook) && <AssetPanel key={`${group.id}-${group.asset_hook || ""}`} group={group} onChanged={onChanged} readOnly={readOnly} />}
      {showBrief && b && (
        <div className="px-4 pb-4 text-[12.5px] text-[#0F1825] leading-relaxed space-y-2 border-t border-[#F0EBDC] pt-3" data-testid={`brief-${group.id}`}>
          <p>{b.summary}</p>
          {(b.asset_angle || b.pipeline_notes) && <p><span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680] mr-2">Asset angle</span>{b.asset_angle || b.pipeline_notes}</p>}
          {(b.platform_fit || b.why_v2e) && <p className="bg-[#FAF7F1] border-l-2 border-[#C84C21] px-3 py-2"><span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#C84C21] mr-2">Platform fit</span>{b.platform_fit || b.why_v2e}</p>}
          {b.role_angles?.length > 0 && (
            <ul className="space-y-1" data-testid={`role-angles-${group.id}`}>
              {b.role_angles.map((a) => <li key={a.role} className="text-[12px] text-[#5C6470]"><span className="text-[#0F1825] font-medium">{a.role}:</span> {a.angle}</li>)}
            </ul>
          )}
        </div>
      )}
      <ul>
        {group.contacts.map((c) => <ContactRow key={c.id} contact={c} onChanged={onChanged} readOnly={readOnly} />)}
      </ul>
    </section>
  );
}

function Pager({ data, onPage }) {
  if (!data || !onPage || (data.pages || 1) <= 1) return null;
  const { page, pages } = data;
  return (
    <div className="flex items-center justify-between gap-3 bg-white border border-[#E8E1D2] px-4 py-2" data-testid="pager">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} className={`${btnLight} !py-1.5`} data-testid="pager-prev"><ChevronLeft size={12} /> Previous</button>
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]" data-testid="pager-label">Page {page} of {pages} · {data.page_size} companies per page · {data.total_companies} compan{data.total_companies === 1 ? "y" : "ies"}{data.q ? " matching" : ""}</span>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} className={`${btnLight} !py-1.5`} data-testid="pager-next">Next <ChevronRight size={12} /></button>
    </div>
  );
}

export function CompanyList({ data, q, onQ, onChanged, readOnly = false, counts, onPage }) {
  const [busy, setBusy] = useState("");
  const companies = data?.companies || [];
  const shown = data?.matching_contacts ?? companies.reduce((n, g) => n + g.contacts.length, 0);
  const bulk = async (action) => {
    const msg = action === "delete"
      ? `Delete every UNSENT contact matching “${q}” — ${shown} match${shown === 1 ? "es" : ""} across all pages? Already-sent records are kept for history (use DNC for those).`
      : `Block all ${shown} contact${shown === 1 ? "" : "s"} matching “${q}” across all pages? They will never be emailed again in any campaign.`;
    if (!window.confirm(msg)) return;
    setBusy(action);
    try {
      const r = await api.bulkByQuery(q, action);
      toast.success(action === "delete" ? `${r.deleted} deleted${r.kept_sent ? ` · ${r.kept_sent} sent record${r.kept_sent === 1 ? "" : "s"} kept` : ""}` : `${r.suppressed} address${r.suppressed === 1 ? "" : "es"} blocked`);
      onChanged?.();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3" data-testid="company-list">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A09680]" />
          <input value={q} onChange={(e) => onQ(e.target.value)} placeholder="Search company, contact, designation or email…" className={`${inputCls} pl-9`} data-testid="search-box" />
        </div>
        {q && shown > 0 && !readOnly && (
          <span className="inline-flex items-center gap-2" data-testid="search-bulk-actions">
            <button type="button" disabled={!!busy} onClick={() => bulk("dnc")} className={btnLight} data-testid="bulk-dnc" title="Suppress every matching address for all campaigns">
              {busy === "dnc" ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />} DNC all {shown}
            </button>
            <button type="button" disabled={!!busy} onClick={() => bulk("delete")} className={btnLight} data-testid="bulk-delete" title="Remove every matching contact that has not been emailed yet (all pages)">
              {busy === "delete" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete unsent
            </button>
          </span>
        )}
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]" data-testid="list-summary">
          {counts ? (
            <>
              <span className="text-[#0F1825]">{counts.companies}</span> compan{counts.companies === 1 ? "y" : "ies"} · <span className="text-[#0F1825]">{counts.total}</span> contacts · <span className="text-[#C84C21]">{counts.drafted}</span> drafted · <span className="text-[#2E7D32]" data-testid="funnel-approved">{counts.approved}</span> approved · <span className="text-[#1D4ED8]" data-testid="funnel-sent">{counts.sent}</span> sent
              {q ? <span> · {shown} matching</span> : null}
            </>
          ) : (
            <>{data?.total_companies ?? companies.length} compan{(data?.total_companies ?? companies.length) === 1 ? "y" : "ies"} · {shown} contacts{q ? " matching" : ""} · {data?.total_contacts ?? 0} total</>
          )}
        </span>
      </div>
      {companies.length === 0 && (
        <div className="bg-white border border-dashed border-[#E8E1D2] p-8 text-center text-[13px] text-[#5C6470]" data-testid="empty-list">
          {q ? "Nothing matches that search." : "No contacts yet — upload a CSV or XLSX above to start."}
        </div>
      )}
      <Pager data={data} onPage={onPage} />
      {companies.map((g) => <CompanyCard key={g.company_key} group={g} onChanged={onChanged} readOnly={readOnly} />)}
      <Pager data={data} onPage={onPage} />
    </div>
  );
}
